import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../db';
import { Send, ArrowLeft, Mail, Smile, Search, CheckCheck, X, Image as ImageIcon, Reply, Users, UserPlus, Settings, LogOut, Edit2, Check } from 'lucide-react';
import UserAvatar from '../components/UserAvatar';
import { toast } from '../utils/feedback';
import { useLocation, useNavigate } from 'react-router-dom';
import { isProfileOnline } from '../utils/onlineStatus';

const Messages = ({ currentProfile }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [friends, setFriends] = useState([]);
  const [activeChat, setActiveChat] = useState(null); // { friendId, friendProfile, convoId }
  const [messages, setMessages] = useState([]);
  const [messageLimit, setMessageLimit] = useState(20);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [participantProfiles, setParticipantProfiles] = useState({});
  
  // Group modal states
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedFriendsForGroup, setSelectedFriendsForGroup] = useState([]);
  const [showGroupSettings, setShowGroupSettings] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  
  const [showGifSelector, setShowGifSelector] = useState(false);
  const [gifs, setGifs] = useState([]);
  const [gifSearch, setGifSearch] = useState('');
  const [isSearchingGifs, setIsSearchingGifs] = useState(false);
  
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  
  const messagesEndRef = useRef(null);
  const lastMessageIdRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const previousScrollHeightRef = useRef(0);
  const isLoadingMoreRef = useRef(false);
  
  const EMOJIS = ['💪', '🔥', '👏', '🙌', '💯', '🐐', '❤️', '😅', '🥵', '🥶', '🤢', '🤮', '🥩', '🍗', '🥗', '🍎', '🤔', '👀', '🤫', '🤬', '🤯', '💀', '🏋️‍♂️', '🏃‍♂️'];
  const BASIC_REACTIONS = ['❤️', '👍', '😂', '😮', '😢', '🙏'];
  
  const fetchGifs = async (query = '') => {
    const apiKey = import.meta.env.VITE_GIPHY_API_KEY;
    if (!apiKey) return;
    setIsSearchingGifs(true);
    try {
      let url = query.trim() 
        ? `https://api.giphy.com/v1/gifs/search?api_key=${apiKey}&q=${encodeURIComponent(query)}&limit=20`
        : `https://api.giphy.com/v1/gifs/trending?api_key=${apiKey}&limit=20`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.meta && data.meta.status !== 200) {
        toast.error("Erreur Giphy: " + data.meta.msg);
        return;
      }
      setGifs(data.data || []);
    } catch (e) {
      console.error("Error fetching GIFs:", e);
    }
    setIsSearchingGifs(false);
  };

  useEffect(() => {
    if (showGifSelector && gifs.length === 0) fetchGifs();
  }, [showGifSelector]);

  // 1. Listen to friends profiles in real-time
  useEffect(() => {
    if (!currentProfile?.friends?.length) {
      setLoading(false);
      return;
    }
    const unsubscribe = db.listenToFriends(currentProfile.friends, (updatedFriends) => {
      setFriends(updatedFriends);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [currentProfile?.friends]);

  // Handle URL param selection for a new chat
  // On mount or location change check if there's a user or convo query param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const userId = params.get('user');
    const convoId = params.get('convo');
    
    if (userId && currentProfile && friends.length > 0) {
      const friend = friends.find(f => f.id === userId);
      if (friend && (!activeChat || activeChat.friendId !== userId)) {
        db.getOrCreateConversation(currentProfile.id, friend.id).then(id => {
          setActiveChat({
            type: 'friend',
            friendId: friend.id,
            friendProfile: friend,
            convoId: id
          });
          // Clean URL to not get stuck on this user, but PRESERVE the hash!
          navigate(`/messages${location.hash}`, { replace: true });
        });
      }
    } else if (convoId && currentProfile && conversations.length > 0) {
      const convo = conversations.find(c => c.id === convoId);
      if (convo && (!activeChat || activeChat.convoId !== convoId)) {
        if (convo.isGroup) {
          setActiveChat({
            type: 'group',
            convo: convo,
            convoId: convo.id
          });
        } else {
          const friendId = convo.participants.find(p => p !== currentProfile.id);
          const friend = friends.find(f => f.id === friendId);
          if (friend) {
            setActiveChat({
              type: 'friend',
              friendId: friend.id,
              friendProfile: friend,
              convoId: convo.id
            });
          }
        }
        navigate(`/messages${location.hash}`, { replace: true });
      }
    }
  }, [location.search, location.hash, currentProfile, friends, conversations, activeChat, navigate]);

  // Update activeChat profile if their online status changes
  useEffect(() => {
    if (activeChat) {
      const updatedFriend = friends.find(f => f.id === activeChat.friendId);
      if (updatedFriend && isProfileOnline(updatedFriend) !== isProfileOnline(activeChat.friendProfile)) {
        setActiveChat(prev => ({ ...prev, friendProfile: updatedFriend }));
      }
    }
  }, [friends]);

  // Listen to all conversations to get unread counts
  useEffect(() => {
    if (!currentProfile) return;
    const unsubscribe = db.listenToConversations(currentProfile.id, (convos) => {
      setConversations(convos);
    });
    return () => unsubscribe();
  }, [currentProfile?.id]);
  
  // Mark as read if conversation is active and has unread messages
  useEffect(() => {
    if (activeChat?.convoId && currentProfile) {
      const convo = conversations.find(c => c.id === activeChat.convoId);
      if (convo?.unreadCount?.[currentProfile.id] > 0) {
        db.markConversationAsRead(activeChat.convoId, currentProfile.id);
      }
    }
  }, [conversations, activeChat, currentProfile]);

  // Fetch profiles for non-friend group members
  useEffect(() => {
    if (activeChat?.type === 'group' && activeChat.convo?.participants) {
      const fetchProfiles = async () => {
        const profiles = { ...participantProfiles };
        let updated = false;
        for (const pId of activeChat.convo.participants) {
          if (!profiles[$pId] && pId !== currentProfile.id) {
            const p = await db.getProfile(pId);
            if (p) {
              profiles[$pId] = p;
              updated = true;
            }
          }
        }
        if (updated) {
          setParticipantProfiles(profiles);
        }
      };
      fetchProfiles();
    }
  }, [activeChat?.convoId, activeChat?.convo?.participants]);

  // Listen to messages when a chat is active
  useEffect(() => {
    if (!activeChat?.convoId) return;

    // Track active conversation on window for notification filtering
    window.activeConvoId = activeChat.convoId;

    // Mark as read
    db.markConversationAsRead(activeChat.convoId, currentProfile.id);

    const unsubscribe = db.listenToMessages(activeChat.convoId, messageLimit, (msgs) => {
      setMessages(msgs);
      setLoadingMessages(false);
      db.markConversationAsRead(activeChat.convoId, currentProfile.id);
    });

    return () => {
      unsubscribe();
      window.activeConvoId = null;
    };
  }, [activeChat?.convoId, currentProfile?.id, messageLimit]);

  // Handle scrolling to specific message or bottom
  useEffect(() => {
    if (messages.length === 0) return;

    if (location.hash && location.hash.startsWith('#msg-')) {
      const el = document.getElementById(location.hash.substring(1));
      if (el) {
        // Remove hash after scrolling
        navigate('/messages', { replace: true });
        return;
      }
    }
    
    // Only scroll to bottom if the newest message is genuinely new
    const currentLastMessage = messages[messages.length - 1];
    if (currentLastMessage && currentLastMessage.id !== lastMessageIdRef.current) {
      lastMessageIdRef.current = currentLastMessage.id;
      const timeout = setTimeout(() => scrollToBottom(), 100);
      return () => clearTimeout(timeout);
    }
  }, [messages, location.hash, navigate]);

  
  // Preserve scroll position when loading older messages
  useLayoutEffect(() => {
    if (isLoadingMoreRef.current && messagesContainerRef.current) {
      const newScrollHeight = messagesContainerRef.current.scrollHeight;
      const oldScrollHeight = previousScrollHeightRef.current;
      
      if (newScrollHeight > oldScrollHeight) {
        messagesContainerRef.current.scrollTop += (newScrollHeight - oldScrollHeight);
        // Mettre à jour la référence au cas où Firebase déclenche plusieurs fois (cache puis serveur)
        previousScrollHeightRef.current = newScrollHeight;
      }
    }
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  // Dynamically derive activeConvo from the latest conversations state
  const activeConvo = activeChat?.type === 'group' 
    ? conversations.find(c => c.id === activeChat.convoId) 
    : activeChat?.convo;

  const typingUsers = activeChat ? (() => {
    const convo = activeConvo;
    if (!convo || !convo.typing) return [];
    return Object.keys(convo.typing)
      .filter(id => id !== currentProfile.id && convo.typing[id] === true)
      .map(id => friends.find(f => f.id === id) || participantProfiles[id] || { name: 'Membre', id });
  })() : [];

  useEffect(() => {
    if (typingUsers.length > 0) {
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [typingUsers.length]);

  // handleSelectFriend is removed because it's replaced by handleSelectChat

  const handleSendGif = async (gifUrl) => {
    if (!activeChat) return;
    setShowGifSelector(false);
    db.sendMessage(activeChat.convoId, currentProfile.id, activeChat.friendId || null, gifUrl, 'gif');
  };

  const typingTimeoutRef = useRef(null);

  const handleMessageChange = (e) => {
    setNewMessage(e.target.value);
    if (activeChat?.convoId) {
      db.setTypingStatus(activeChat.convoId, currentProfile.id, true);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        db.setTypingStatus(activeChat.convoId, currentProfile.id, false);
      }, 2000);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeChat) return;

    const textToSend = newMessage.trim();
    setNewMessage(''); // optimistic clear
    
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    db.setTypingStatus(activeChat.convoId, currentProfile.id, false);
    
    db.sendMessage(activeChat.convoId, currentProfile.id, activeChat.friendId || null, textToSend, 'text', replyingTo?.id);
    setReplyingTo(null);
  };

  const insertEmoji = (emoji) => {
    if (!inputRef.current) return;
    const cursorPosition = inputRef.current.selectionStart;
    const textBeforeCursor = newMessage.slice(0, cursorPosition);
    const textAfterCursor = newMessage.slice(cursorPosition);
    
    setNewMessage(textBeforeCursor + emoji + textAfterCursor);
    setShowEmojiPicker(false);
    setTimeout(() => {
      inputRef.current.focus();
      inputRef.current.selectionStart = cursorPosition + emoji.length;
      inputRef.current.selectionEnd = cursorPosition + emoji.length;
    }, 0);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !activeChat) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image est trop volumineuse (max 5MB)");
      e.target.value = '';
      return;
    }
    toast.info("Envoi de l'image...");
    try {
      const url = await db.uploadMessageMedia(file);
      db.sendMessage(activeChat.convoId, currentProfile.id, activeChat.friendId || null, url, 'photo', replyingTo?.id);
      setReplyingTo(null);
      toast.success("Image envoyée !");
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'envoi de l'image");
    }
    e.target.value = '';
  };

  const handleReaction = async (messageId, emoji) => {
    if (!activeChat) return;
    await db.toggleMessageReaction(activeChat.convoId, messageId, currentProfile.id, emoji);
    setHoveredMessageId(null);
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Chargement de tes amis...</div>;
  }

  const formatMessageTime = (ts) => {
    return new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };
  
  const formatMessageDate = (ts) => {
    const d = new Date(ts);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (d.toDateString() === today.toDateString()) return "Aujourd'hui";
    if (d.toDateString() === yesterday.toDateString()) return "Hier";
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  
  // Calculate sidebar items (friends + groups)
  const chatItems = [];
  
  // Add direct conversations and friends without conversation yet
  friends.forEach(friend => {
    const convo = conversations.find(c => !c.isGroup && c.participants?.includes(friend.id));
    chatItems.push({
      type: 'friend',
      id: friend.id,
      name: friend.name,
      avatar: friend.photoUrl || friend.avatarUrl,
      convo: convo,
      updatedAt: convo?.updatedAt || 0,
      friendProfile: friend,
      unreadCount: convo?.unreadCount?.[currentProfile.id] || 0
    });
  });
  
  // Add group conversations
  conversations.filter(c => c.isGroup).forEach(convo => {
    chatItems.push({
      type: 'group',
      id: convo.id,
      name: convo.name || "Groupe",
      avatar: null, // Will render group icon
      convo: convo,
      updatedAt: convo.updatedAt || 0,
      participants: convo.participants,
      adminId: convo.adminId,
      unreadCount: convo.unreadCount?.[currentProfile.id] || 0
    });
  });
  
  // Sort by updatedAt descending
  chatItems.sort((a, b) => b.updatedAt - a.updatedAt);
  
  const handleCreateGroup = async () => {
    if (newGroupName.trim() === '' || selectedFriendsForGroup.length === 0) {
      toast.error("Entre un nom et sélectionne au moins un ami.");
      return;
    }
    try {
      const convoId = await db.createGroupConversation(newGroupName.trim(), currentProfile.id, selectedFriendsForGroup);
      setShowGroupModal(false);
      setNewGroupName('');
      setSelectedFriendsForGroup([]);
      
      const newConvo = { isGroup: true, id: convoId, name: newGroupName.trim(), adminId: currentProfile.id, participants: [currentProfile.id, ...selectedFriendsForGroup] };
      setActiveChat({
        type: 'group',
        convo: newConvo,
        convoId
      });
      toast.success("Groupe créé !");
    } catch (err) {
      toast.error("Erreur lors de la création du groupe.");
    }
  };

  const handleSelectChat = async (item) => {
    setMessages([]); // Clear previous messages while loading
    if (item.type === 'friend') {
      const convoId = await db.getOrCreateConversation(currentProfile.id, item.friendProfile.id);
      setActiveChat({
        type: 'friend',
        friendId: item.friendProfile.id,
        friendProfile: item.friendProfile,
        convoId
      });
    } else if (item.type === 'group') {
      setActiveChat({
        type: 'group',
        convo: item.convo,
        convoId: item.convo.id
      });
    }
  };

  return (
    <div style={{ display: 'flex', gap: '1rem', height: 'calc(100vh - 150px)', minHeight: '500px' }}>
      
      {/* Sidebar: Friends List */}
      <div style={{
        flex: '0 0 300px',
        backgroundColor: 'var(--bg-card)',
        borderRadius: 'var(--border-radius)',
        border: '1px solid var(--border-color)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }} className="messages-sidebar">
        <div style={{ padding: '1rem', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Mail size={24} color="var(--primary-color)" />
            Discussions
          </h2>
          <button 
            className="btn-icon"
            onClick={() => setShowGroupModal(true)}
            title="Nouveau groupe"
            style={{ color: 'var(--primary-color)' }}
          >
            <UserPlus size={24} />
          </button>
        </div>
        
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {chatItems.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              Tu n'as pas encore d'amis à qui parler ! Va dans l'onglet Social.
            </div>
          ) : (
            chatItems.map(item => (
              <div 
                key={item.type === 'group' ? `group_${item.id}` : `friend_${item.id}`}
                onClick={() => handleSelectChat(item)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '1rem',
                  cursor: 'pointer',
                  backgroundColor: activeChat?.convoId === item.convo?.id && item.convo?.id ? 'rgba(168, 159, 207, 0.1)' : 'transparent',
                  borderBottom: '1px solid var(--border-color)',
                  transition: 'background-color 0.2s'
                }}
              >
                {item.type === 'friend' ? (
                  <UserAvatar profile={item.friendProfile} size={40} />
                ) : (
                  item.convo?.avatarUrl ? (
                    <img loading="lazy" src={item.convo.avatarUrl} alt={item.name} style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'var(--primary-color)', display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#fff' }}>
                      <Users size={20} />
                    </div>
                  )
                )}
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <div style={{ fontWeight: '600', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    {item.name}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    {item.type === 'friend' ? (
                      isProfileOnline(item.friendProfile) ? <span style={{color: 'var(--secondary-color)'}}>En ligne</span> : 'Hors ligne'
                    ) : (
                      `${item.participants?.length || 0} membres`
                    )}
                  </div>
                </div>
                {item.unreadCount > 0 && activeChat?.convoId !== item.convo?.id && (
                  <div style={{
                    backgroundColor: 'var(--danger-color)',
                    color: 'white',
                    borderRadius: '12px',
                    padding: '0.1rem 0.5rem',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    marginLeft: 'auto'
                  }}>
                    {item.unreadCount}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div style={{
        flex: 1,
        backgroundColor: 'var(--bg-card)',
        borderRadius: 'var(--border-radius)',
        border: '1px solid var(--border-color)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }} className="messages-chat-area">
        
        {activeChat ? (
          <>
            {/* Chat Header */}
            <div style={{ padding: '1rem', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '1rem', backgroundColor: 'var(--bg-card)' }}>
              <button 
                className="btn-icon mobile-only" 
                onClick={() => setActiveChat(null)}
                style={{ padding: '0.5rem' }}
              >
                <ArrowLeft size={24} />
              </button>
              {activeChat.type === 'friend' ? (
                <>
                  <UserAvatar profile={activeChat.friendProfile} size={40} />
                  <h3 style={{ margin: 0 }}>{activeChat.friendProfile.name}</h3>
                </>
              ) : (
                <>
                  <div 
                    style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'var(--primary-color)', display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#fff', cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', overflow: 'hidden' }} 
                    onClick={() => setShowGroupSettings(true)}
                    title="Paramètres du groupe"
                  >
                    {activeChat.convo?.avatarUrl ? (
                      <img loading="lazy" src={activeChat.convo.avatarUrl} alt="Group" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <Users size={20} />
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <h3 style={{ margin: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }} onClick={() => setShowGroupSettings(true)}>
                      {activeConvo?.name}
                      <Settings size={14} color="var(--text-secondary)" />
                    </h3>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{activeConvo?.participants?.length || 0} membres</span>
                  </div>
                </>
              )}
            </div>

            {/* Messages Feed */}
            <div ref={messagesContainerRef} style={{ flex: 1, overflowY: 'auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0', position: 'relative' }}>
              {loadingMessages ? (
                <div style={{ textAlign: 'center', color: 'var(--text-secondary)', marginTop: '2rem' }}>
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Chargement...</span>
                  </div>
                </div>
              ) : messages.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text-secondary)', marginTop: '2rem' }}>
                  Envoyez un premier message pour commencer la discussion !
                </div>
              ) : (
                <>
                  {messages.length >= messageLimit && (
                    <div style={{ textAlign: 'center', margin: '1rem 0' }}>
                      <button 
                        onClick={() => {
                          if (messagesContainerRef.current) {
                            previousScrollHeightRef.current = messagesContainerRef.current.scrollHeight;
                            isLoadingMoreRef.current = true;
                            // Reset flag after 1.5s to allow for Firebase double-fires (cache then server)
                            setTimeout(() => { isLoadingMoreRef.current = false; }, 1500);
                          }
                          setMessageLimit(prev => prev + 20);
                        }}
                        className="btn btn-secondary"
                        style={{ 
                          padding: '0.6rem 1.5rem', 
                          fontSize: '0.85rem',
                          borderRadius: '24px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          fontWeight: '500',
                          backgroundColor: 'var(--secondary-color)',
                          color: '#1e1b26',
                          border: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto'
                        }}
                        onMouseOver={(e) => { e.currentTarget.style.filter = 'brightness(1.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                        onMouseOut={(e) => { e.currentTarget.style.filter = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
                      >
                        Charger les messages précédents
                      </button>
                    </div>
                  )}
                  {messages.map((msg, index) => {
                  const isMe = msg.senderId === currentProfile.id;
                  const showDate = index === 0 || new Date(messages[index-1].timestamp).toDateString() !== new Date(msg.timestamp).toDateString();
                  const isLastMessage = index === messages.length - 1;
                  const isReadByFriend = isLastMessage && isMe && activeConvo?.unreadCount?.[activeChat.friendId] === 0;

                  const nextMsg = messages[index + 1];
                  const isLastInGroup = !nextMsg || 
                                        nextMsg.senderId !== msg.senderId || 
                                        (new Date(nextMsg.timestamp).getTime() - new Date(msg.timestamp).getTime() > 5 * 60 * 1000) ||
                                        new Date(nextMsg.timestamp).toDateString() !== new Date(msg.timestamp).toDateString();

                  return (
                    <React.Fragment key={msg.id}>
                      {showDate && (
                        <div style={{ textAlign: 'center', margin: '1rem 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                          <span style={{ backgroundColor: 'var(--bg-color)', padding: '0.2rem 0.8rem', borderRadius: '12px' }}>
                            {formatMessageDate(msg.timestamp)}
                          </span>
                        </div>
                      )}
                      
                      {msg.type === 'system' ? (
                        <div style={{ alignSelf: 'center', margin: '0.5rem 0', fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'center', backgroundColor: 'var(--bg-color)', padding: '0.3rem 1rem', borderRadius: '16px' }}>
                          {msg.text}
                        </div>
                      ) : (
                      <div 
                        onMouseEnter={() => {
                          if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(hover: hover)').matches) {
                            setHoveredMessageId(msg.id);
                          }
                        }}
                        onMouseLeave={() => {
                          if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(hover: hover)').matches) {
                            setHoveredMessageId(null);
                          }
                        }}
                        onClick={() => {
                          if (typeof window !== 'undefined' && window.matchMedia && !window.matchMedia('(hover: hover)').matches) {
                            if (msg.type !== 'photo') {
                              setHoveredMessageId(prev => (prev === msg.id ? null : msg.id));
                            }
                          }
                        }}
                        id={`msg-${msg.id}`}
                        style={{
                          alignSelf: isMe ? 'flex-end' : 'flex-start',
                          maxWidth: '75%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: isMe ? 'flex-end' : 'flex-start',
                          marginBottom: isLastInGroup ? '0.8rem' : '0.2rem',
                          position: 'relative'
                        }}>
                        
                        {msg.replyTo && messages.find(m => m.id === msg.replyTo) && (
                          <div style={{
                            fontSize: '0.75rem',
                            color: 'var(--text-secondary)',
                            backgroundColor: 'var(--bg-color)',
                            padding: '0.3rem 0.6rem',
                            borderRadius: '8px',
                            marginBottom: '4px',
                            opacity: 0.8,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <Reply size={12} />
                            {messages.find(m => m.id === msg.replyTo).type === 'photo' ? 'Photo' : 
                             messages.find(m => m.id === msg.replyTo).type === 'gif' ? 'GIF' : 
                             messages.find(m => m.id === msg.replyTo).text.substring(0, 30)}
                          </div>
                        )}
                        
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem', flexDirection: isMe ? 'row-reverse' : 'row' }}>
                          {!isMe && activeChat.type === 'group' && (
                            <div 
                              style={{ width: '24px', flexShrink: 0, cursor: 'pointer' }}
                              onClick={() => navigate(`/profile/${msg.senderId}`)}
                            >
                              {isLastInGroup && (
                                <UserAvatar profile={friends.find(f => f.id === msg.senderId) || participantProfiles[msg.senderId] || { name: '?' }} size={24} />
                              )}
                            </div>
                          )}
                          <div style={{
                            backgroundColor: (msg.type === 'gif' || msg.type === 'photo') ? 'transparent' : (isMe ? 'var(--primary-color)' : 'var(--bg-color)'),
                            color: isMe ? '#fff' : 'var(--text-primary)',
                            padding: (msg.type === 'gif' || msg.type === 'photo') ? '0' : '0.8rem 1rem',
                            borderRadius: '16px',
                            borderBottomRightRadius: (msg.type === 'gif' || msg.type === 'photo') ? '16px' : (isMe ? '4px' : '16px'),
                            borderBottomLeftRadius: (msg.type === 'gif' || msg.type === 'photo') ? '16px' : (!isMe ? '4px' : '16px'),
                            wordBreak: 'break-word',
                            overflow: 'hidden'
                          }}>
                            {(!isMe && activeChat.type === 'group' && (!messages[index - 1] || messages[index - 1].senderId !== msg.senderId || showDate)) && (
                              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginBottom: '0.2rem', fontWeight: 'bold' }}>
                                {friends.find(f => f.id === msg.senderId)?.name || participantProfiles[msg.senderId]?.name || 'Membre'}
                              </div>
                            )}
                            {msg.type === 'gif' || msg.type === 'photo' ? (
                              <img 
                                loading="lazy" 
                                src={msg.text} 
                                alt={msg.type} 
                                style={{ 
                                  maxWidth: '100%', 
                                  maxHeight: '260px', 
                                  borderRadius: '14px', 
                                  objectFit: 'cover', 
                                  display: 'block',
                                  cursor: msg.type === 'photo' ? 'pointer' : 'default',
                                  touchAction: 'manipulation'
                                }} 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (msg.type === 'photo') {
                                    setFullscreenImage(msg.text);
                                  }
                                }} 
                              />
                            ) : (
                              msg.text
                            )}
                          </div>
                        </div>
                        
                        {hoveredMessageId === msg.id && (
                          <div 
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              gap: '4px', 
                              backgroundColor: 'var(--bg-card)', 
                              padding: '4px 8px', 
                              borderRadius: '20px', 
                              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                              border: '1px solid var(--border-color)',
                              marginTop: '4px',
                              marginBottom: '2px',
                              alignSelf: isMe ? 'flex-end' : 'flex-start',
                              zIndex: 10,
                              animation: 'fadeIn 0.15s ease-out'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button 
                              type="button"
                              className="btn-icon" 
                              style={{ padding: '4px', width: '28px', height: '28px', minWidth: '28px', minHeight: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
                              onClick={(e) => {
                                e.stopPropagation();
                                setReplyingTo(msg);
                                setHoveredMessageId(null);
                              }}
                              title="Répondre"
                            >
                              <Reply size={16} />
                            </button>
                            <div style={{ height: '16px', width: '1px', backgroundColor: 'var(--border-color)', margin: '0 2px' }} />
                            <div style={{ display: 'flex', gap: '4px' }}>
                              {BASIC_REACTIONS.map(emoji => (
                                <button 
                                  type="button"
                                  key={emoji} 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReaction(msg.id, emoji);
                                  }} 
                                  style={{ 
                                    background: 'none', 
                                    border: 'none', 
                                    cursor: 'pointer', 
                                    fontSize: '1.15rem', 
                                    padding: '2px 4px', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    borderRadius: '6px'
                                  }}
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                          <div style={{
                            display: 'flex',
                            gap: '4px',
                            marginTop: '-8px',
                            zIndex: 1,
                            backgroundColor: 'var(--bg-card)',
                            padding: '2px 6px',
                            borderRadius: '12px',
                            border: '1px solid var(--border-color)',
                            alignSelf: isMe ? 'flex-end' : 'flex-start',
                            marginRight: isMe ? '10px' : '0',
                            marginLeft: isMe ? '0' : '10px'
                          }}>
                            {Object.values(msg.reactions).reduce((acc, emoji) => {
                              const existing = acc.find(r => r.emoji === emoji);
                              if (existing) existing.count++;
                              else acc.push({ emoji, count: 1 });
                              return acc;
                            }, []).map((r, i) => (
                              <span key={i} style={{ fontSize: '0.8rem' }}>{r.emoji} {r.count > 1 ? r.count : ''}</span>
                            ))}
                          </div>
                        )}

                        {isLastInGroup && (
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.2rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                            {formatMessageTime(msg.timestamp)}
                            {isReadByFriend && <CheckCheck size={14} color="var(--primary-color)" />}
                          </div>
                        )}
                      </div>
                      )}
                    </React.Fragment>
                  );
                })}
                </>
              )}
              {typingUsers.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', alignSelf: 'flex-start', marginBottom: '0.5rem' }}>
                  {typingUsers.map((u, i) => (
                    <UserAvatar key={i} profile={u} size="24px" />
                  ))}
                  <div className="typing-indicator">
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div style={{ position: 'relative' }}>
              {replyingTo && (
                <div style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: 'var(--bg-color)',
                  borderTop: '1px solid var(--border-color)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '0.9rem',
                  color: 'var(--text-secondary)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Reply size={16} />
                    <span>Réponse à <strong>{replyingTo.senderId === currentProfile.id ? 'toi-même' : activeChat.friendProfile.name}</strong> : {replyingTo.type === 'photo' ? 'Photo' : replyingTo.type === 'gif' ? 'GIF' : replyingTo.text.substring(0, 50)}</span>
                  </div>
                  <button type="button" className="btn-icon" onClick={() => setReplyingTo(null)} style={{ padding: '4px', minWidth: '24px', minHeight: '24px', width: 'auto', height: 'auto' }}><X size={16}/></button>
                </div>
              )}
              
              {/* Overlay for closing popups */}
              {(showGifSelector || showEmojiPicker) && (
                <div 
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9 }}
                  onClick={() => { setShowGifSelector(false); setShowEmojiPicker(false); }}
                />
              )}
              
              {showGifSelector && (
                <div style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: '1rem',
                  width: '300px',
                  height: '350px',
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--border-radius)',
                  boxShadow: '0 -5px 20px rgba(0,0,0,0.3)',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  zIndex: 10
                }}>
                  <div style={{ padding: '0.8rem', display: 'flex', gap: '0.5rem', borderBottom: '1px solid var(--border-color)' }}>
                    <div style={{ flex: 1, position: 'relative' }}>
                      <input 
                        type="text" 
                        placeholder="Chercher un GIF..." 
                        value={gifSearch}
                        onChange={(e) => setGifSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && fetchGifs(gifSearch)}
                        style={{ width: '100%', padding: '0.5rem 0.5rem 0.5rem 2rem', borderRadius: '15px', border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-color)', color: 'var(--text-primary)' }}
                      />
                      <Search size={14} style={{ position: 'absolute', left: '0.6rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
                    </div>
                    <button type="button" className="btn-icon" onClick={() => fetchGifs(gifSearch)} disabled={isSearchingGifs}>
                      <Search size={18} />
                    </button>
                    <button type="button" className="btn-icon" onClick={() => setShowGifSelector(false)}>
                      <X size={18} />
                    </button>
                  </div>
                  <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center' }}>
                    {isSearchingGifs ? (
                      <div style={{ padding: '2rem', textAlign: 'center', width: '100%' }}>Recherche...</div>
                    ) : gifs.map(gif => (
                      <img loading="lazy" 
                        key={gif.id} 
                        src={gif.images.fixed_height_small.url} 
                        alt={gif.title}
                        onClick={() => handleSendGif(gif.images.fixed_height.url)}
                        style={{ height: '100px', cursor: 'pointer', borderRadius: '8px', objectFit: 'cover' }}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSendMessage} style={{ 
                padding: '1rem', 
                borderTop: '1px solid var(--border-color)',
                display: 'flex',
                gap: '0.5rem',
                alignItems: 'center'
              }}>
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  style={{ display: 'none' }} 
                  accept="image/*" 
                  onChange={handleImageUpload} 
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="btn-icon"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <ImageIcon size={24} />
                </button>
                <div className="hide-on-mobile" style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="btn-icon"
                    style={{ color: showEmojiPicker ? 'var(--primary-color)' : 'var(--text-secondary)' }}
                  >
                    <Smile size={24} />
                  </button>
                  {showEmojiPicker && (
                    <div style={{
                      position: 'absolute',
                      bottom: '100%',
                      left: '0',
                      marginBottom: '10px',
                      backgroundColor: 'var(--bg-card)',
                      padding: '1rem',
                      borderRadius: 'var(--border-radius)',
                      boxShadow: '0 -5px 20px rgba(0,0,0,0.3)',
                      border: '1px solid var(--border-color)',
                      zIndex: 10,
                      width: '280px',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(6, 1fr)',
                      gap: '0.5rem'
                    }}>
                      {EMOJIS.map(emoji => (
                        <button
                          key={emoji}
                          type="button"
                          style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', padding: '0.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}
                          onClick={() => insertEmoji(emoji)}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(168, 159, 207, 0.2)'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setShowGifSelector(!showGifSelector)}
                  className="btn-icon"
                  style={{ 
                    color: showGifSelector ? 'var(--primary-color)' : 'var(--text-secondary)'
                  }}
                >
                  <div style={{ fontSize: '0.65rem', fontWeight: 'bold', border: '2px solid currentColor', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>GIF</div>
                </button>
                <input
                  type="text"
                  ref={inputRef}
                  value={newMessage}
                  onChange={handleMessageChange}
                  placeholder="Ton message..."
                  style={{
                    flex: 1,
                    padding: '0.8rem 1rem',
                    borderRadius: '24px',
                    border: '1px solid var(--border-color)',
                    backgroundColor: 'var(--bg-color)',
                    color: 'var(--text-primary)'
                  }}
                />
                <button 
                  type="submit" 
                  disabled={!newMessage.trim()}
                  style={{
                    backgroundColor: newMessage.trim() ? 'var(--secondary-color)' : 'var(--bg-color)',
                    color: newMessage.trim() ? '#fff' : 'var(--text-secondary)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '45px',
                    height: '45px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: newMessage.trim() ? 'pointer' : 'default',
                    transition: 'background-color 0.2s',
                    flexShrink: 0
                  }}
                >
                  <Send size={20} />
                </button>
              </form>
            </div>
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
            Sélectionne une discussion pour commencer.
          </div>
        )}
      </div>

      {/* Group Creation Modal */}
      {showGroupModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ backgroundColor: 'var(--bg-card)', padding: '2rem', borderRadius: '12px', width: '90%', maxWidth: '400px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>Nouveau Groupe</h2>
              <button className="btn-icon" onClick={() => setShowGroupModal(false)}><X size={24} /></button>
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Nom du groupe</label>
              <input 
                type="text" 
                className="input-field" 
                placeholder="Ex: Les sportifs"
                value={newGroupName}
                onChange={e => setNewGroupName(e.target.value)}
                style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-color)', color: 'var(--text-primary)' }}
              />
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Sélectionner des amis</label>
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '0.5rem' }}>
                {friends.length === 0 ? (
                  <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Tu n'as pas encore d'amis.</div>
                ) : (
                  friends.map(friend => (
                    <label key={friend.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem', cursor: 'pointer', borderBottom: '1px solid var(--border-color)' }}>
                      <input 
                        type="checkbox" 
                        checked={selectedFriendsForGroup.includes(friend.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedFriendsForGroup(prev => [...prev, friend.id]);
                          } else {
                            setSelectedFriendsForGroup(prev => prev.filter(id => id !== friend.id));
                          }
                        }}
                      />
                      <UserAvatar profile={friend} size={30} />
                      <span>{friend.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
            
            <button 
              className="btn btn-primary" 
              style={{ width: '100%', marginTop: '1rem' }} 
              onClick={handleCreateGroup}
            >
              <Check size={18} /> Créer le groupe
            </button>
          </div>
        </div>
      )}

      {/* Group Settings Modal */}
      {showGroupSettings && activeChat?.type === 'group' && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ backgroundColor: 'var(--bg-card)', padding: '2rem', borderRadius: '12px', width: '90%', maxWidth: '400px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>Paramètres du groupe</h2>
              <button className="btn-icon" onClick={() => setShowGroupSettings(false)}><X size={24} /></button>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '1.5rem' }}>
              <div style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: 'var(--primary-color)', display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#fff', marginBottom: '0.5rem', overflow: 'hidden', position: 'relative' }}>
                {activeConvo?.avatarUrl ? (
                  <img loading="lazy" src={activeConvo.avatarUrl} alt="Group" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <Users size={40} />
                )}
                {activeConvo?.adminId === currentProfile.id && (
                  <label style={{ position: 'absolute', bottom: 0, right: 0, left: 0, backgroundColor: 'rgba(0,0,0,0.5)', padding: '4px 0', cursor: 'pointer', textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
                    <Edit2 size={12} color="#fff" />
                    <input 
                      type="file" 
                      accept="image/*" 
                      style={{ display: 'none' }}
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        if (file.size > 2 * 1024 * 1024) {
                          toast.error("L'image est trop volumineuse (max 2MB)");
                          return;
                        }
                        try {
                          toast.info("Envoi de l'image...");
                          const url = await db.uploadGroupPicture(activeChat.convoId, file);
                          await db.updateGroupAvatar(activeChat.convoId, url);
                          toast.success("Photo de groupe mise à jour !");
                        } catch (error) {
                          console.error("Group avatar upload error:", error);
                          toast.error("Erreur lors de la mise à jour");
                        }
                      }}
                    />
                  </label>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Nom du groupe</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input 
                  type="text" 
                  className="input-field" 
                  defaultValue={activeConvo?.name}
                  disabled={activeConvo?.adminId !== currentProfile.id}
                  id="group-name-input"
                  style={{ flex: 1, padding: '0.8rem', borderRadius: '8px', border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-color)', color: 'var(--text-primary)' }}
                />
                {activeConvo?.adminId === currentProfile.id && (
                  <button 
                    className="btn-icon"
                    style={{ width: '45px', height: '45px', borderRadius: '50%', padding: 0, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={async () => {
                      const newName = document.getElementById('group-name-input').value.trim();
                      if (newName && newName !== activeConvo?.name) {
                        await db.updateGroupName(activeChat.convoId, newName);
                        toast.success("Nom modifié !");
                      }
                    }}
                  >
                    <Edit2 size={16} />
                  </button>
                )}
              </div>
              {activeConvo?.adminId !== currentProfile.id && (
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>Seul le créateur peut renommer le groupe.</div>
              )}
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Ajouter des membres</label>
              <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '0.5rem' }}>
                {friends.filter(f => !activeConvo?.participants?.includes(f.id)).length === 0 ? (
                  <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Tous tes amis sont déjà dans le groupe.</div>
                ) : (
                  friends.filter(f => !activeConvo?.participants?.includes(f.id)).map(friend => (
                    <div key={friend.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem', borderBottom: '1px solid var(--border-color)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <UserAvatar profile={friend} size={30} />
                        <span>{friend.name}</span>
                      </div>
                      <button 
                        className="btn btn-secondary"
                        style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem', minHeight: 'auto' }}
                        onClick={async () => {
                          await db.addMembersToGroup(activeChat.convoId, [friend.id]);
                          toast.success(`${friend.name} ajouté(e) !`);
                        }}
                      >
                        <UserPlus size={14} /> Ajouter
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Membres actuels ({activeConvo?.participants?.length || 0})</label>
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '0.5rem' }}>
                {activeConvo?.participants?.map(pid => {
                  const isMe = pid === currentProfile.id;
                  const profile = isMe ? currentProfile : (friends.find(fr => fr.id === pid) || participantProfiles[pid] || { name: 'Membre' });
                  const isAdmin = pid === activeConvo?.adminId;
                  
                  return (
                    <div key={pid} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem', borderBottom: '1px solid var(--border-color)' }}>
                      <div 
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: isMe ? 'default' : 'pointer' }}
                        onClick={() => !isMe && navigate(`/profile/${pid}`)}
                      >
                        <UserAvatar profile={profile} size={30} />
                        <span>{profile.name} {isMe ? '(Toi)' : ''}</span>
                      </div>
                      {isAdmin && (
                        <span style={{ fontSize: '0.7rem', backgroundColor: 'var(--primary-color)', color: 'white', padding: '0.2rem 0.5rem', borderRadius: '10px' }}>
                          Admin
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            
            <button 
              className="btn btn-danger"
              style={{ width: '100%', marginTop: '1rem' }} 
              onClick={() => setShowLeaveConfirm(true)}
            >
              <LogOut size={18} /> Quitter le groupe
            </button>
          </div>
        </div>
      )}

      {/* Leave Group Confirmation Modal */}
      {showLeaveConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 110, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ backgroundColor: 'var(--bg-card)', padding: '2rem', borderRadius: '12px', width: '90%', maxWidth: '400px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>Quitter le groupe</h2>
              <button className="btn-icon" onClick={() => setShowLeaveConfirm(false)}><X size={24} /></button>
            </div>
            
            <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.5' }}>
              Es-tu sûr de vouloir quitter <strong>{activeConvo?.name}</strong> ? Tu ne pourras plus y accéder.
            </p>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button 
                className="btn"
                style={{ backgroundColor: 'var(--bg-color)', border: '1px solid var(--border-color)' }}
                onClick={() => setShowLeaveConfirm(false)}
              >
                Annuler
              </button>
              <button 
                className="btn btn-danger"
                onClick={async () => {
                  await db.leaveGroup(activeChat.convoId, currentProfile.id, currentProfile.name);
                  setShowLeaveConfirm(false);
                  setShowGroupSettings(false);
                  setActiveChat(null);
                  toast.success("Tu as quitté le groupe.");
                }}
              >
                <LogOut size={16} style={{ marginRight: '0.5rem' }} /> Quitter
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .messages-sidebar {
            display: ${activeChat ? 'none' : 'flex'} !important;
            flex: 1 !important;
          }
          .messages-chat-area {
            display: ${!activeChat ? 'none' : 'flex'} !important;
          }
          .mobile-only {
            display: block !important;
          }
        }
      `}</style>
      
      {fullscreenImage && createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.92)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            zIndex: 9999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'calc(env(safe-area-inset-top, 0px) + 20px) 16px calc(env(safe-area-inset-bottom, 0px) + 20px) 16px',
            touchAction: 'none'
          }}
          onClick={() => setFullscreenImage(null)}
        >
          <button 
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setFullscreenImage(null);
            }}
            aria-label="Fermer"
            style={{
              position: 'absolute',
              top: 'max(16px, calc(env(safe-area-inset-top, 0px) + 12px))',
              right: 'max(16px, calc(env(safe-area-inset-right, 0px) + 12px))',
              color: '#ffffff',
              backgroundColor: 'rgba(30, 30, 30, 0.75)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              borderRadius: '50%',
              width: '44px',
              height: '44px',
              minWidth: '44px',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 10000000,
              boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
              touchAction: 'manipulation'
            }}
          >
            <X size={24} />
          </button>
          <img 
            src={fullscreenImage} 
            alt="Plein écran" 
            style={{
              maxWidth: 'min(94vw, 700px)',
              maxHeight: 'calc(85dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
              objectFit: 'contain',
              cursor: 'default',
              borderRadius: '12px',
              boxShadow: '0 12px 40px rgba(0,0,0,0.7)',
              userSelect: 'none',
              WebkitUserSelect: 'none'
            }} 
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body
      )}
    </div>
  );
};

export default Messages;


